/**
 * AUTHENTICATION STATE — Svelte 5 Runes
 *
 * This is the SINGLE SOURCE OF TRUTH for authentication in the entire app.
 * Any component that needs to know "is the user logged in?" or "what role
 * do they have?" imports from this file.
 *
 * KEY SVELTE 5 CONCEPTS USED:
 * - $state()    = Reactive variable. When it changes, all consumers re-render.
 * - $derived()  = Computed value. Recalculates automatically when its deps change.
 * - $effect()   = Side effect. Runs whenever its tracked dependencies change.
 *
 * WHY .svelte.ts? The `.svelte.ts` extension tells the Svelte compiler to
 * process this file with runes support, even though it's not a component.
 * Without it, $state/$derived/$effect won't work in plain .ts files.
 */
import { getAccessToken, setAccessToken, setRefreshToken, setStoredUser, getStoredUser, clearAllAuthData, } from '../../core/utils/storage';
import { login as apiLogin, logout as apiLogout } from './auth.service';
// ==========================================================================
// REACTIVE STATE (these are the "atoms" of our auth state)
// ==========================================================================
// The currently logged-in user (null = not logged in)
// $state() makes this reactive — any component reading `authState.user` will
// automatically re-render when this value changes.
let user = $state(getStoredUser());
// Loading flag — true while login/logout API call is in flight
let isLoading = $state(false);
// Error message from the last failed login attempt
let error = $state(null);
// ==========================================================================
// DERIVED STATE (computed automatically from the atoms above)
// ==========================================================================
// Is the user currently authenticated?
// $derived() recalculates whenever `user` changes. No manual subscription needed.
const isAuthenticated = $derived(user !== null && getAccessToken() !== null);
// Does the current user have admin privileges?
const isAdmin = $derived(user?.role === 'admin');
// ==========================================================================
// ACTIONS (functions that mutate the state)
// ==========================================================================
async function login(credentials) {
    isLoading = true;
    error = null;
    try {
        const response = await apiLogin(credentials);
        // Persist tokens and user to localStorage so they survive page refresh
        setAccessToken(response.accessToken);
        setRefreshToken(response.refreshToken);
        setStoredUser(response.user);
        // Update reactive state — this triggers re-renders across the app
        user = response.user;
        return true; // Signal success to the caller (Login.svelte)
    }
    catch (err) {
        error = err instanceof Error ? err.message : 'Login gagal. Coba lagi.';
        return false;
    }
    finally {
        isLoading = false;
    }
}
async function logout() {
    isLoading = true;
    try {
        await apiLogout();
    }
    catch {
        // Even if the API call fails, we still clear local state
        // (the token might already be expired on the server)
    }
    finally {
        clearAllAuthData();
        user = null;
        error = null;
        isLoading = false;
        // Redirect to login page
        window.location.hash = '#/login';
    }
}
function clearError() {
    error = null;
}
// ==========================================================================
// EXPORTED STATE OBJECT
// WHY a single object? So every consumer does `authState.user`, `authState.login()`.
// This prevents naming collisions and makes imports clean.
// ==========================================================================
export const authState = {
    // Reactive getters — use these in components to READ state
    get user() { return user; },
    get isLoading() { return isLoading; },
    get error() { return error; },
    get isAuthenticated() { return isAuthenticated; },
    get isAdmin() { return isAdmin; },
    // Actions — use these to CHANGE state
    login,
    logout,
    clearError,
};
//# sourceMappingURL=auth.state.svelte.js.map